import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Lock, Upload, Camera, Loader, Save } from 'lucide-react';
import axios from 'axios';
import Swal from 'sweetalert2';

const ProfilePage = () => {
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [passwords, setPasswords] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [driverData, setDriverData] = useState(null);
  const token = localStorage.getItem('driverToken');


  useEffect(() => {
    // Load driver data from localStorage
    const storedData = localStorage.getItem('driverData');
    if (storedData) {
      setDriverData(JSON.parse(storedData));
    }
  }, []);

  const handlePasswordChange = (e) => {
    setPasswords({
      ...passwords,
      [e.target.name]: e.target.value
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      // Create preview for the image
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadProfileImage = async () => {
    if (!profileImage || !driverData) {
      Swal.fire({
        icon: 'error',
        title: 'No image selected or driver data missing',
        text: 'Please select an image to upload and ensure you are logged in'
      });
      return;
    }
  
    setLoading(true);
    const formData = new FormData();
    formData.append('file', profileImage);
    formData.append('upload_preset', 'africfreshupload');
  
    try {
      // Upload image to Cloudinary
      const cloudinaryResponse = await axios.post(
        'https://api.cloudinary.com/v1_1/dfehxeb5r/image/upload',
        formData
      );
  
      const imageUrl = cloudinaryResponse.data.secure_url;
      
      // Update driver data in the backend using email identifier - CORRECTED VERSION
      const response = await axios.put(
        'https://afric-fresh-backend.onrender.com/driver/profile',
        {
          email: driverData.email,
          profileImage: imageUrl
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
  
      // Update local storage
      const updatedDriverData = {...driverData, profileImage: imageUrl};
      localStorage.setItem('driverData', JSON.stringify(updatedDriverData));
      setDriverData(updatedDriverData);
      
      Swal.fire({
        icon: 'success',
        title: 'Profile image updated!',
        text: 'Your profile image has been successfully updated'
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      Swal.fire({
        icon: 'error',
        title: 'Upload failed',
        text: error.response?.data?.message || 'Failed to upload profile image. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async () => {
    if (!driverData) {
      Swal.fire({
        icon: 'error',
        title: 'Not logged in',
        text: 'Please login to update your password'
      });
      return;
    }
  
    // Validate passwords
    if (passwords.newPassword !== passwords.confirmPassword) {
      Swal.fire({
        icon: 'error',
        title: 'Passwords do not match',
        text: 'Please make sure your passwords match'
      });
      return;
    }
  
    if (passwords.newPassword.length < 6) {
      Swal.fire({
        icon: 'error',
        title: 'Password too short',
        text: 'Password must be at least 6 characters long'
      });
      return;
    }
  
    setLoading(true);
    try {
      // Call API to update password using email identifier - CORRECTED VERSION
      await axios.put(
        'https://afric-fresh-backend.onrender.com/driver/password',
        {
          email: driverData.email,
          newPassword: passwords.newPassword
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      Swal.fire({
        icon: 'success',
        title: 'Password updated!',
        text: 'Your password has been successfully updated'
      });
      
      // Clear password fields
      setPasswords({
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Error updating password:', error);
      Swal.fire({
        icon: 'error',
        title: 'Update failed',
        text: error.response?.data?.message || 'Failed to update password. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!driverData) {
    return <div className="p-6 flex justify-center"><Loader className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">Profile</h1>
      <div className="flex space-x-4 border-b">
        <button
          onClick={() => setActiveTab('details')}
          className={`px-4 py-2 ${
            activeTab === 'details' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-500'
          }`}
        >
          Details
        </button>
        <button
          onClick={() => setActiveTab('image')}
          className={`px-4 py-2 ${
            activeTab === 'image' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-500'
          }`}
        >
          Profile Image
        </button>
        <button
          onClick={() => setActiveTab('password')}
          className={`px-4 py-2 ${
            activeTab === 'password' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-500'
          }`}
        >
          Change Password
        </button>
      </div>

      {activeTab === 'details' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-6 rounded-lg shadow-sm"
        >
          <div className="flex justify-center mb-6">
            {driverData.profileImage ? (
              <img 
                src={driverData.profileImage} 
                alt="Profile" 
                className="w-32 h-32 rounded-full object-cover border-4 border-gray-200"
              />
            ) : (
              <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center">
                <User className="w-16 h-16 text-gray-400" />
              </div>
            )}
          </div>
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <User className="w-6 h-6 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="text-lg font-semibold text-gray-800">{driverData.name}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <User className="w-6 h-6 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="text-lg font-semibold text-gray-800">{driverData.email}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <User className="w-6 h-6 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600">Phone</p>
                <p className="text-lg font-semibold text-gray-800">{driverData.phone}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <User className="w-6 h-6 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600">Vehicle Type</p>
                <p className="text-lg font-semibold text-gray-800">{driverData.vehicleType}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <User className="w-6 h-6 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600">Plate Number</p>
                <p className="text-lg font-semibold text-gray-800">{driverData.plateNumber}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <User className="w-6 h-6 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600">License Number</p>
                <p className="text-lg font-semibold text-gray-800">{driverData.licenseNumber}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <User className="w-6 h-6 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600">Bank Name</p>
                <p className="text-lg font-semibold text-gray-800">{driverData.bankName}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <User className="w-6 h-6 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600">Account Name</p>
                <p className="text-lg font-semibold text-gray-800">{driverData.accountName}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <User className="w-6 h-6 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600">Account Number</p>
                <p className="text-lg font-semibold text-gray-800">{driverData.accountNumber}</p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {activeTab === 'image' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-6 rounded-lg shadow-sm"
        >
          <div className="space-y-6">
            <div className="flex flex-col items-center space-y-4">
              <Camera className="w-10 h-10 text-gray-500" />
              <p className="text-lg font-semibold text-gray-800">Profile Image</p>
              
              {/* Preview image */}
              <div className="w-40 h-40 rounded-full overflow-hidden border-4 border-gray-200">
                {previewImage ? (
                  <img src={previewImage} alt="Preview" className="w-full h-full object-cover" />
                ) : driverData.profileImage ? (
                  <img src={driverData.profileImage} alt="Current profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                    <User className="w-20 h-20 text-gray-400" />
                  </div>
                )}
              </div>
              
              <div className="w-full max-w-md">
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Select new profile image
                </label>
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={handleImageChange}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100"
                />
              </div>
              
              <button
                onClick={uploadProfileImage}
                disabled={loading || !profileImage}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Save Profile Image</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {activeTab === 'password' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-6 rounded-lg shadow-sm"
        >
          <div className="space-y-6">
            <div className="flex flex-col items-center space-y-4">
              <Lock className="w-10 h-10 text-gray-500" />
              <p className="text-lg font-semibold text-gray-800">Change Password</p>
              
              <div className="w-full max-w-md space-y-4">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    New Password
                  </label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwords.newPassword}
                    onChange={handlePasswordChange}
                    placeholder="Enter new password"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwords.confirmPassword}
                    onChange={handlePasswordChange}
                    placeholder="Confirm new password"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <button
                onClick={updatePassword}
                disabled={loading || !passwords.newPassword || !passwords.confirmPassword}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Update Password</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default ProfilePage;